/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot } from '@angular/router';
import {
  CommunicationRestService,
  CommunicationType,
  ContactRestService,
  ContactType,
  JobCommunicationRestService,
  JobLetterDocRestService,
  JobChecklistsRestService,
  JobRestService,
  LetterRestService,
  reportIdField,
  SalesFolderStatus,
  SystemRoleBit,
  systemRolesBitSet,
  UserRestService,
  JobChecklistProduceDto,
} from '@gh/builder-shared-data';
import {
  AppRouter,
  dictionaryItemFromDto,
  DictionaryService,
  getRouteParam, globalDictionaries,
  InitViewService,
  isRouteForCreationMode,
} from '@gh/common-shared';
import { AutocompleteItem, ID, PrintEntryPoint } from '@gh/common-shared-data';
import { FilteredDataQuery, getSlicedData } from '@gh/core-data';
import { filter$, map$, switchMap$ } from '@gh/rx/operators';
import { Observable } from 'rxjs';
import { JobAdmin, JobDppDetailsGeneral } from './job.model';
import {
  CommunicationData,
  CommunicationListItem,
  JobChecklist,
  JobChecklistItem,
  Letter,
  letterListFromDto,
  LetterListItem,
} from './job-details-tab';
import { GridLayoutService } from '@gh/core-ui-grid';
import { jobSpecCreateDialogRoute, REPORT_JOB_DETAILS, ReportService } from '@gh/builder-shared';
import { DialogService, NotificationService } from '@gh/core-ui';
import { openInNewWindow, toString } from '@gh/core-util';
import { translationTuple } from '@gh/core-mls';

function getParentJobId(route: ActivatedRouteSnapshot): ID {
  if (route.parent) {
    return route.parent.params['jobId'];
  } else {
    throw new Error('JobDetailsTabBusinessService: route parent is not defined');
  }
}

@Injectable()
export class JobDPPBusinessService implements InitViewService {
  jobId: ID;
  jobDetails: JobDppDetailsGeneral;
  reportUrl: string;
  communicationData: CommunicationData;

  constructor(private jobRest: JobRestService,
              private userRest: UserRestService,
              private gridLayoutService: GridLayoutService,
              private dictionaryService: DictionaryService,
              private lettersRestService: JobLetterDocRestService,
              private contactRestService: ContactRestService,
              private jobChecklistsService: JobChecklistsRestService,
              private letterRest: LetterRestService,
              private notifications: NotificationService,
              private communicationService: CommunicationRestService,
              private jobChecklistsRestService: JobChecklistsRestService,
              private communicationRestService: JobCommunicationRestService,
              private dialogs: DialogService,
              private report: ReportService,
              private router: AppRouter,
              private dictionaries: DictionaryService) {
  }

  initView(route: ActivatedRouteSnapshot): any {
    const jobId = getRouteParam(route, 'jobId');
    this.jobId = jobId;
    return {
      details: isRouteForCreationMode(route) ? void 0 : this.get(jobId).pipe(map$((res) => {
        this.jobDetails = res;
        return res;
      })),
      reportUrl: this.report.generateUrl(REPORT_JOB_DETAILS, [reportIdField('Job', jobId)]).pipe(map$((res) => {
        this.reportUrl = res;
        return res;
      })),
      communicationData: this.getCommunicationData().pipe(map$((res) => {
        this.communicationData = res;
        return res;
      })),
    };
  }

  initLettersDocsTabView(route: ActivatedRouteSnapshot): any {
    return {
      jobId: getParentJobId(route),
      letterList: this.getLetters(),
      gridLayout: this.gridLayoutService.get('jobsLettersDocs'),
    };
  }

  initChecklistsTabView(route: ActivatedRouteSnapshot): any {
    return {
      jobId: getParentJobId(route),
      checklists: this.getChecklists(),
      gridLayout: this.gridLayoutService.get('jobsChecklists'),
    };
  }

  initGeneralTabView(route: ActivatedRouteSnapshot): any {
    return {
      jobId: getParentJobId(route),
      statusDict: this.dictionaries.getByEnum(SalesFolderStatus),
      notProceedingReasons: this.dictionaries.getDictionaryFromGlobal(globalDictionaries.notProceedingReasons)
        .pipe(map$((items) =>  items ? items.map(dictionaryItemFromDto) : [])),
    };
  }

  initCommunicationListTabView(route: ActivatedRouteSnapshot): any {
    return {
      communicationTypes: this.dictionaryService.getByEnum(CommunicationType),
      gridLayout: this.gridLayoutService.get('jobsCommunication'),
    };
  }

  get(jobId: ID): Observable<JobDppDetailsGeneral> {
    return this.jobRest.getDpp(jobId).pipe(map$((dpp) => ({
      ...dpp,
      county: { id: dpp.countyId, name: dpp.countyName },
    })));
  }

  update(job: JobDppDetailsGeneral): Observable<JobDppDetailsGeneral> {
    return this.jobRest.updateDpp({
      id: job.id,
      contractAmount: job.contractAmount,
      memo: job.memo,
      jobAdminId: job.jobAdmin ? job.jobAdmin.userId : void 0,
      etag: job.etag,
      siteName: job.siteName,
      siteLotNumber: job.siteLotNumber,
      siteMapReference: job.siteMapReference,
      siteBlockNumber: job.siteBlockNumber,
      siteStreet: job.siteStreet,
      siteStreetNumber: job.siteStreetNumber,
      siteSuburb: job.siteSuburb,
      sitePostcode: job.sitePostcode,
      siteCity: job.siteCity,
      siteState: job.siteState,
      countyId: job.county?.id,
    }).pipe(map$((res) => {
      const model = {
        ...res,
        county: {id: res.countyId, name: res.countyName},
      };
      this.jobDetails = model;
      return model;
    }));
  }

  findAllJobAdminsByName(query: FilteredDataQuery<string>): Observable<JobAdmin[]> {
    return this.userRest.findAllAsDataSource({
      name: query.filter,
      autocomplete: true,
      showInactive: false,
      systemRoles: systemRolesBitSet(SystemRoleBit.JobAdmin),
    }).select(query).pipe(map$(getSlicedData));
  }

  getLetters(): Observable<LetterListItem[]> {
    return this.lettersRestService.get(this.jobId).pipe(map$(letterListFromDto));
  }

  produceLetter(entryPoint: PrintEntryPoint, letter: LetterListItem): Observable<Letter> {
    return this.lettersRestService.produce(this.jobId, {
      jobDocName: letter.fileName,
      entryPoint,
    });
  }

  deleteLetter(letter: Letter): Observable<void> {
    return this.letterRest.remove(letter);
  }
  buildFinalVersion(letter: Letter, contactId: ID): Observable<Letter> {
    return this.letterRest.buildFinalVersion({
                                               contactId,
                                               jobId: this.jobId,
                                               filledTemplate: letter,
                                             });
  }
  openDocs(): void {
    openInNewWindow(this.communicationData.gDriveUrl);
  }

  openSpec(dirty?: boolean): void {
    if (dirty) {
      this.notifications.error('msg_save_first');
      return;
    }
    if (this.jobDetails.isSpecExist && !this.jobDetails.isNotProceeding && !this.jobDetails.isFinalized) {
      this.router.navigateToChild([`/job-admin/jobs/${this.jobDetails.id}/spec`],
                                  { queryParams: { forDpp: true },
                                    parentTitle: translationTuple('lbl_job_number_with_param', {
                                      number: toString(this.jobDetails.jobNumber),
                                    })});
    } else if (this.jobDetails.isNotProceeding || this.jobDetails.isFinalized) {
      this.notifications.error('msg_spec_empty');
    } else {
      this.dialogs.openWithInput(jobSpecCreateDialogRoute, {isFromCreateBtn: false})
        .afterClosed().pipe(
        filter$(Boolean))
        .subscribe((formValue: any) => {
          this.router.navigateToChild(
            [`/job-admin/jobs/${this.jobId}/spec/new`],
            {
              queryParams: { specTemplateId: formValue.jobSpecTemplate.id, forDpp: true },
              parentTitle: translationTuple('lbl_job_number_with_param', { number: toString(this.jobDetails.jobNumber) }),
            });
        });
    }
  }

  getCommunicationDetails(id: ID): Observable<CommunicationListItem> {
    return this.communicationService.get(id);
  }

  getCommunicationData(): Observable<CommunicationData> {
    return this.communicationRestService.get(this.jobId);
  }

  getContacts(
    query: FilteredDataQuery<string>,
    types: ContactType[] = [ContactType.Employee, ContactType.CreditorSupplier],
  ): Observable<AutocompleteItem[]> {
    return this.contactRestService.findAllAsDataSource({
                                                         types,
                                                         autocomplete: true,
                                                         showInactive: false,
                                                         name: query.filter,
                                                       }).select(query).pipe(
      map$(getSlicedData));
  }

  getChecklists(): Observable<JobChecklist> {
    return this.jobChecklistsService.get(this.jobId);
  }

  produceChecklist(entryPoint: PrintEntryPoint, checklist: JobChecklistItem): Observable<JobChecklistProduceDto> {
    return this.jobChecklistsRestService.generateImage(this.jobId, checklist.id)
      .pipe(switchMap$(() =>  this.jobChecklistsService.produce(this.jobId, checklist.id)));
  }
}
